import { CommandExecutionContext, SelfContainedCommand } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { googleDriveAdapter } from '../../../services/integrations/adapters/GoogleDriveAdapter';
import { resolveGoogleDriveIntegration } from '../../../services/integrations/utils/integrationResolver';

const log = createFileLogger('GOOGLE_SHEET_READ');

export const googleSheetReadCommand: SelfContainedCommand = {
  id: 'google_sheet_read',
  description: `Read data from a Google Sheet

Returns cell values from the specified range using A1 notation.

**Parameters:**
- spreadsheetId: Google Sheets spreadsheet ID (from URL)
- range: A1 notation range (e.g., "Sheet1!A1:B10", "Sheet1!A:A", "Sheet1!1:5")
- integrationId (optional): Integration ID (auto-detected if omitted)

**A1 Notation Examples:**
- "Sheet1!A1:B10" - Read cells A1 through B10 on Sheet1
- "Sheet1!A:A" - Read entire column A on Sheet1
- "Sheet1!1:5" - Read rows 1 through 5 on Sheet1
- "A1:C3" - Read cells A1 through C3 on default sheet

**Example URL:**
- https://docs.google.com/spreadsheets/d/1ABC...xyz/edit → spreadsheetId is "1ABC...xyz"

**Output:**
Returns 2D array of cell values with row/column counts.`,
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'spreadsheetId',
        type: 'string',
        description: 'Google Sheets spreadsheet ID from URL (e.g., "1ABC...xyz" from docs.google.com/spreadsheets/d/1ABC...xyz/edit)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Spreadsheet ID cannot be empty';
          return null;
        }
      },
      {
        name: 'range',
        type: 'string',
        description: 'A1 notation range (e.g., "Sheet1!A1:B10", "Sheet1!A:A")',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Range cannot be empty';
          return null;
        }
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'Integration ID (auto-detected if omitted)',
        required: false
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { spreadsheetId: '1ABC123xyz', range: 'Sheet1!A1:B10' },
        expectedOutput: createSuccessResult('Successfully read Google Sheet', {
          values: [['Name', 'Age'], ['Alice', 30]],
          row_count: 2,
          column_count: 2
        }),
        description: 'Read a range from a Google Sheet',
        context: 'When you need to read spreadsheet data'
      }
    ],
    bad: [
      {
        input: { spreadsheetId: '1ABC123xyz', range: '' },
        error: 'Range cannot be empty',
        why: 'Range is required',
        fix: 'Provide a valid A1 notation range'
      }
    ]
  },
  
  performance: {
    expectedDuration: 2000,
    complexity: 'medium'
  },
  
  formatViewData: (result: any) => {
    // Format for canonical system UI rendering
    const data = result.data || {};
    return {
      type: 'google_sheet_read',
      summary: `Read ${data.row_count || 0} rows × ${data.column_count || 0} columns from range "${data.range}"`,
      data: {
        spreadsheet_id: data.spreadsheet_id,
        range: data.range,
        values: data.values || [],
        row_count: data.row_count || 0,
        column_count: data.column_count || 0
      },
      meta: {
        timestamp: Date.now()
      }
    };
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let { spreadsheetId, range, integrationId } = params;
      
      if (!spreadsheetId) {
        return createErrorResult('Missing spreadsheet ID', 'spreadsheetId parameter is required', 'validation');
      }
      
      if (!range) {
        return createErrorResult('Missing range', 'range parameter is required', 'validation');
      }
      
      log.log('[google_sheet_read] Reading Google Sheet:', { spreadsheetId, range, hasIntegrationId: !!integrationId });
      
      // TODO: The AI often fails on the first attempt because it guesses sheet names (e.g., "Sheet1!A:Z")
      // that don't exist. Consider enhancing this command to:
      // 1. First fetch available sheet names from the spreadsheet metadata
      // 2. Provide sheet names in the tool description or as a separate parameter
      // 3. Default to omitting sheet name prefix (works for first/active sheet)
      // Current workaround: AI retries with different range formats until one succeeds
      
      // Check if spreadsheetId is an llmId (entity reference) and resolve to actual Drive ID
      if (spreadsheetId.includes('_')) {
        // Looks like an llmId - try to resolve it to actual driveFileId
        log.log('[google_sheet_read] 🔍 Detected llmId, resolving to driveFileId:', spreadsheetId);
        try {
          const useEntityStore = (await import('../../../stores/entityStore')).default;
          const allEntities = useEntityStore.getState().entities;
          log.log('[google_sheet_read] 🔍 EntityStore has', allEntities.length, 'entities');
          
          const entity = allEntities.find((e: any) => e.llmId === spreadsheetId);
          log.log('[google_sheet_read] 🔍 Found entity:', { 
            found: !!entity, 
            hasLlmId: entity?.llmId,
            hasSkeleton: !!entity?.skeleton,
            hasDriveFileId: !!entity?.skeleton?.driveFileId,
            driveFileId: entity?.skeleton?.driveFileId,
            entityType: entity?.entityType
          });
          
          if (entity && entity.skeleton?.driveFileId) {
            log.log('[google_sheet_read] ✅ Resolved llmId to driveFileId:', { llmId: spreadsheetId, driveFileId: entity.skeleton.driveFileId });
            spreadsheetId = entity.skeleton.driveFileId;
          } else {
            log.warn('[google_sheet_read] ❌ Could not resolve llmId to driveFileId:', { 
              spreadsheetId, 
              hasEntity: !!entity,
              hasSkeleton: !!entity?.skeleton,
              skeletonKeys: entity?.skeleton ? Object.keys(entity.skeleton) : []
            });
          }
        } catch (error) {
          log.error('[google_sheet_read] ❌ Error resolving llmId:', error);
          // Continue with original spreadsheetId - might still work if it's a real ID
        }
      } else {
        log.log('[google_sheet_read] 💡 spreadsheetId does not look like llmId (no underscore):', spreadsheetId);
      }
      
      // Resolve integration ID if not provided
      let resolvedIntegrationId = integrationId;
      if (!resolvedIntegrationId) {
        try {
          resolvedIntegrationId = await resolveGoogleDriveIntegration(context.userId);
          log.log('[google_sheet_read] Auto-resolved integration ID:', resolvedIntegrationId);
        } catch (error) {
          return createErrorResult(
            'No Google Drive integration found',
            error instanceof Error ? error.message : 'Please connect Google Drive in Settings → Integrations',
            'validation'
          );
        }
      }
      
      // Read sheet via adapter
      const result = await googleDriveAdapter.readGoogleSheet(
        resolvedIntegrationId,
        spreadsheetId,
        range
      );
      
      log.log('[google_sheet_read] Successfully read sheet:', {
        range: result.range,
        rows: result.row_count,
        columns: result.column_count
      });
      
      return createSuccessResult(
        `Successfully read Google Sheet range "${result.range}" (${result.row_count} rows × ${result.column_count} columns)`,
        {
          spreadsheet_id: result.spreadsheet_id,
          range: result.range,
          values: result.values,
          row_count: result.row_count,
          column_count: result.column_count
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'google_sheet_read'
          } as any
        }
      );
      
    } catch (error) {
      log.error('[google_sheet_read] Error reading Google Sheet:', error);
      return createErrorResult(
        'Failed to read Google Sheet',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  }
};

